import { motion } from 'framer-motion';
import {
    ArrowRight,
    Building2,
    Shield,
    User,
    Users
} from 'lucide-react';
import { Link } from 'react-router-dom';

const DigitalEcosystem = () => {
    return (
        <div className="min-h-screen pt-20">
            {/* Hero Section */}
            <section className="py-20 bg-surface-100">
                <div className="container-custom">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-16"
                    >
                        <h1 className="text-4xl lg:text-5xl font-bold text-white mb-6">
                            Digital ID Ecosystem
                        </h1>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                            Build a comprehensive network of trusted digital identity issuers and verifiers. Create an ecosystem where credentials flow seamlessly between organizations.
                        </p>
                    </motion.div>

                    <div className="grid lg:grid-cols-3 gap-8 mb-16">
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6 }}
                            className="glass rounded-2xl p-8 border border-white/20 text-center"
                        >
                            <div className="w-16 h-16 bg-blue-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                                <Building2 className="w-8 h-8 text-blue-400" />
                            </div>
                            <h2 className="text-xl font-bold text-white mb-4">Trusted Issuers</h2>
                            <p className="text-gray-300 mb-6">Universities, employers, and certification bodies become verified credential issuers in your ecosystem.</p>
                            <div className="space-y-3">
                                <div className="bg-blue-500/10 rounded-lg p-3 border border-blue-500/30">
                                    <div className="text-white font-medium text-sm">Educational Institutions</div>
                                    <div className="text-gray-400 text-xs">Degrees, Certificates, Transcripts</div>
                                </div>
                                <div className="bg-green-500/10 rounded-lg p-3 border border-green-500/30">
                                    <div className="text-white font-medium text-sm">Employers</div>
                                    <div className="text-gray-400 text-xs">Employment History, Skills</div>
                                </div>
                                <div className="bg-purple-500/10 rounded-lg p-3 border border-purple-500/30">
                                    <div className="text-white font-medium text-sm">Licensing Bodies</div>
                                    <div className="text-gray-400 text-xs">Professional Licenses</div>
                                </div>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 50 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="glass rounded-2xl p-8 border border-white/20 text-center"
                        >
                            <div className="w-16 h-16 bg-green-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                                <Users className="w-8 h-8 text-green-400" />
                            </div>
                            <h2 className="text-xl font-bold text-white mb-4">Credential Holders</h2>
                            <p className="text-gray-300 mb-6">Individuals securely manage and share their digital credentials across the entire ecosystem.</p>
                            <div className="bg-gradient-to-br from-green-500/10 to-blue-500/10 rounded-xl p-6 border border-white/20">
                                <div className="text-center">
                                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-3">
                                        <User className="w-6 h-6 text-gray-800" />
                                    </div>
                                    <div className="text-white font-medium mb-2">John Doe</div>
                                    <div className="text-gray-300 text-sm mb-3">Digital Identity Wallet</div>
                                    <div className="flex justify-center space-x-1">
                                        <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                                        <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                                        <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                                        <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                                    </div>
                                    <div className="text-gray-400 text-xs mt-2">4 Active Credentials</div>
                                </div>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: 50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6, delay: 0.4 }}
                            className="glass rounded-2xl p-8 border border-white/20 text-center"
                        >
                            <div className="w-16 h-16 bg-purple-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                                <Shield className="w-8 h-8 text-purple-400" />
                            </div>
                            <h2 className="text-xl font-bold text-white mb-4">Trusted Verifiers</h2>
                            <p className="text-gray-300 mb-6">Organizations that need to verify credentials integrate seamlessly with the ecosystem infrastructure.</p>
                            <div className="space-y-3">
                                <div className="bg-orange-500/10 rounded-lg p-3 border border-orange-500/30">
                                    <div className="text-white font-medium text-sm">Healthcare Providers</div>
                                    <div className="text-gray-400 text-xs">Medical Credentials, Licensing</div>
                                </div>
                                <div className="bg-red-500/10 rounded-lg p-3 border border-red-500/30">
                                    <div className="text-white font-medium text-sm">Financial Services</div>
                                    <div className="text-gray-400 text-xs">KYC, Identity Verification</div>
                                </div>
                                <div className="bg-indigo-500/10 rounded-lg p-3 border border-indigo-500/30">
                                    <div className="text-white font-medium text-sm">Government Agencies</div>
                                    <div className="text-gray-400 text-xs">Citizen Services, Benefits</div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Call to Action */}
            <section className="py-20 bg-gradient-to-r from-primary-600 to-neon-600">
                <div className="container-custom text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                            Ready to Build Your Digital ID Ecosystem?
                        </h2>
                        <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                            Connect issuers, holders, and verifiers in a trusted network of digital identity.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <a href="https://app.passhub.documint.id/signup" target="_blank" rel="noopener noreferrer" className="bg-white text-primary-600 hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold transition-colors duration-200 inline-flex items-center">
                                Start Free Trial
                                <ArrowRight size={20} className="ml-2" />
                            </a>
                            <Link to="/contact" className="border border-white/20 text-white hover:bg-white/10 px-8 py-3 rounded-lg font-semibold transition-colors duration-200 inline-flex items-center">
                                Contact Sales
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default DigitalEcosystem;